import { SeriesModelLink } from '@/components/shows/series-model-link'
import { useState, type ReactNode } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  XAxis,
  YAxis,
} from 'recharts'
import { ArrowRight, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import { Input } from '@/components/ui/input'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getCategoryBadgeClass, getCategoryLabel } from '@/lib/classColors'
import {
  annualRows,
  csvCell,
  category,
  decomposition,
  format,
  palette,
  percent,
  share,
  visibleReport,
  type Report,
  type Trend,
  type Year,
} from './model'
import './dashboard.css'

const teal = '#147d73'
const config = {
  entries: { label: 'Entries', color: teal },
  entrants: { label: 'Entrants', color: '#7b9d92' },
  entriesIndex: { label: 'Entries index', color: teal },
  entrantsIndex: { label: 'Entrants index', color: '#a16207' },
  value: { label: 'Value', color: teal },
  active: { label: 'Active classes', color: teal },
  empty: { label: 'Zero-entry classes', color: '#b8cdc6' },
  entryShare: { label: 'Cumulative entries %', color: teal },
}
const axis = { tickLine: false, axisLine: false, tick: { fontSize: 12 }, tickMargin: 8 }
const metricLabels = [
  ['entriesCount', 'Entries'],
  ['entrantsCount', 'Entrants'],
  ['averageEntriesPerEntrant', 'Entries per entrant'],
  ['clubsCount', 'Participating clubs'],
] as const
function Panel({
  title,
  description,
  children,
  aside,
}: {
  title: string
  description?: string
  children: ReactNode
  aside?: ReactNode
}) {
  return (
    <Card className="series-panel min-w-0">
      <CardHeader className="flex flex-wrap flex-row items-start justify-between gap-3">
        <div className="space-y-2">
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </div>
        {aside}
      </CardHeader>
      <CardContent className="space-y-4 min-w-0">{children}</CardContent>
    </Card>
  )
}
function DataTable({
  caption,
  headers,
  rows,
}: {
  caption: string
  headers: string[]
  rows: ReactNode[][]
}) {
  return (
    <Table tabIndex={0}>
      <caption className="sr-only">{caption}</caption>
      <TableHeader>
        <TableRow>
          {headers.map(h => (
            <TableHead key={h} scope="col">
              {h}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row, i) => (
          <TableRow key={i}>
            {row.map((cell, j) =>
              j === 0 ? (
                <TableHead key={j} scope="row">
                  {cell}
                </TableHead>
              ) : (
                <TableCell key={j}>{cell}</TableCell>
              )
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
function TableDetail({ children }: { children: ReactNode }) {
  return (
    <details>
      <summary className="cursor-pointer text-sm underline py-2">View data table</summary>
      {children}
    </details>
  )
}
function ClassBadge({ cell }: { cell: Trend['cells'][number]['class'] }) {
  return cell ? (
    <Badge variant="outline" className={getCategoryBadgeClass(category(cell))}>
      {cell.code} · {getCategoryLabel(category(cell), cell.division.name)}
    </Badge>
  ) : null
}
function useReportFilter(key: string, fallback: string) {
  const [params, setParams] = useSearchParams()
  return [
    params.get(key) ?? fallback,
    (value: string) => {
      setParams(
        previous => {
          const next = new URLSearchParams(previous)
          next.set(key, value)
          return next
        },
        { replace: true }
      )
    },
  ] as const
}
function AnnualTable({ report, seriesId }: { report: Report; seriesId: string }) {
  const location = useLocation()
  const availableYears = report.years.filter(y => y.edition).map(y => y.year)
  const matchingYears = (year: number) => {
    const start = Math.max(
      0,
      Math.min(availableYears.indexOf(year) - 4, availableYears.length - 10)
    )
    return availableYears.slice(start, start + 10).join(',')
  }
  return (
    <DataTable
      caption="Annual participation — live persisted entries"
      headers={['Year / edition', ...metricLabels.map(m => m[1]), 'Alignment coverage']}
      rows={report.years.map(({ year, edition }) =>
        !edition
          ? [year, 'No available edition data', '—', '—', '—', '—']
          : [
              <>
                {year} <span className="block">{edition.name}</span>{' '}
                {edition.provisional && <span className="block">Provisional</span>}
              </>,
              ...metricLabels.map(([key]) => format(edition.metrics[key])),
              <>
                <ul>
                  {(['assigned', 'unresolved', 'unmatched'] as const).map(state => (
                    <li key={state}>
                      {state === 'assigned'
                        ? 'Confirmed'
                        : state === 'unmatched'
                          ? 'Excluded from comparison'
                          : 'Needs review'}
                      : {edition.metrics.coverage[state].classes} classes /{' '}
                      {edition.metrics.coverage[state].entries} entries
                    </li>
                  ))}
                </ul>
                {Boolean(edition.reviewReasons?.length) && (
                  <p className="text-xs">
                    Review:{' '}
                    {edition.reviewReasons!.map(reason => reason.replaceAll('_', ' ')).join(', ')}
                  </p>
                )}
                {edition.canEdit && (
                  <Link
                    className="underline"
                    to={`/show-series/${seriesId}/alignment?years=${matchingYears(year)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                    aria-label={`Match classes across years from ${year}`}
                  >
                    Match classes across years
                  </Link>
                )}
              </>,
            ]
      )}
    />
  )
}
function Overview({
  report,
  seriesId,
  explore,
}: {
  report: Report
  seriesId: string
  explore: (id?: string) => void
}) {
  const [mode, setMode] = useState('entries')
  const editions = report.years.filter(y => y.edition)
  const latest = editions.at(-1),
    previous = editions.at(-2)
  const m = latest?.edition?.metrics,
    before = previous?.edition?.metrics
  const growth = decomposition(previous, latest)
  const data = annualRows(report)
  const movers = report.trends
    .flatMap(t => {
      const current = t.cells.find(c => c.year === latest?.year)?.class
      const prior = t.cells.find(c => c.year === previous?.year)?.class
      return current && prior ? [{ ...t, current, prior, delta: current.count - prior.count }] : []
    })
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
    .slice(0, 5)
  return (
    <div className="space-y-5">
      <div className="series-kpis">
        {metricLabels.map(([key, label]) => {
          const value = m?.[key] ?? null,
            old = before?.[key] ?? null
          const delta =
            value === null || old === null
              ? null
              : old > 0
                ? (100 * (value - old)) / old
                : value === 0
                  ? 0
                  : null
          return (
            <div key={key} className="series-kpi">
              <p className="text-sm text-muted-foreground">
                {label} · {latest?.year ?? '—'}
              </p>
              <p className="series-value">{format(value)}</p>
              <p className="series-note">
                {latest?.edition?.provisional || previous?.edition?.provisional
                  ? 'Provisional comparison'
                  : previous
                    ? `${delta !== null && delta > 0 ? '+' : ''}${percent(delta)} vs ${previous.year}`
                    : 'No earlier available edition'}
              </p>
            </div>
          )
        })}
      </div>
      <div className="series-grid">
        <Panel
          title="Participation over time"
          description="Independent populations for each edition. Gaps remain gaps."
        >
          <div role="group" aria-label="Participation measure" className="series-controls">
            {(
              [
                ['entries', 'Entries'],
                ['entrants', 'Entrants'],
                ['index', 'Indexed growth'],
              ] as const
            ).map(([value, label]) => (
              <Button
                key={value}
                size="sm"
                variant="outline"
                className="series-soft-button"
                aria-pressed={mode === value}
                onClick={() => setMode(value)}
              >
                {label}
              </Button>
            ))}
          </div>
          <ChartContainer
            role="group"
            config={config}
            className="h-72 w-full"
            aria-label="Annual participation; exact values in annual table"
          >
            <AreaChart data={data} accessibilityLayer>
              <CartesianGrid vertical={false} />
              <XAxis dataKey="year" {...axis} />
              <YAxis {...axis} width={45} />
              <ChartTooltip content={<ChartTooltipContent />} />
              {mode === 'index' ? (
                <>
                  <Area
                    dataKey="entriesIndex"
                    stroke={teal}
                    fill={teal}
                    fillOpacity={0.08}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Area
                    dataKey="entrantsIndex"
                    stroke="#a16207"
                    fill="none"
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                </>
              ) : (
                <Area
                  dataKey={mode}
                  stroke={teal}
                  fill={teal}
                  fillOpacity={0.12}
                  connectNulls={false}
                  dot
                  isAnimationActive={false}
                />
              )}
            </AreaChart>
          </ChartContainer>
          <p className="series-note">
            {mode === 'index'
              ? `First available edition (${editions[0]?.year ?? '—'}) = 100. A zero baseline has no index. Teal: entries; amber: entrants.`
              : 'All persisted entries except not-entered, including drafts.'}
          </p>
          {mode === 'index' && (
            <TableDetail>
              <DataTable
                caption="Indexed participation"
                headers={['Year', 'Entries index', 'Entrants index']}
                rows={data.map(d => [d.year, format(d.entriesIndex), format(d.entrantsIndex)])}
              />
            </TableDetail>
          )}
        </Panel>
        <Panel
          title="How the entry total changed"
          description={
            previous && latest
              ? `${previous.year} → ${latest.year} · total entries`
              : 'Requires two available finalized editions'
          }
        >
          {growth ? (
            <>
              <div className="series-value">
                {growth.total > 0 ? '+' : ''}
                {format(growth.total)}{' '}
                <span className="text-base tracking-normal font-normal">entries</span>
              </div>
              {[
                ['From entrant count', growth.breadth],
                ['From average entries per entrant', growth.depth],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between gap-4 border-b py-4">
                  <span>{label}</span>
                  <strong className="whitespace-nowrap">
                    {Number(value) > 0 ? '+' : ''}
                    {format(Number(value))} entries
                  </strong>
                </div>
              ))}
              <p className="series-note">
                Average entries per entrant:{' '}
                {format(previous!.edition!.metrics.averageEntriesPerEntrant)} in {previous!.year} →{' '}
                {format(latest!.edition!.metrics.averageEntriesPerEntrant)} in {latest!.year}.
              </p>
              <p className="series-note">
                At the {previous!.year} average, the{' '}
                {format(latest!.edition!.metrics.entrantsCount)} entrants in {latest!.year} would
                have submitted{' '}
                {format(
                  latest!.edition!.metrics.entrantsCount *
                    previous!.edition!.metrics.averageEntriesPerEntrant!
                )}{' '}
                entries. The actual total was {format(latest!.edition!.metrics.entriesCount)}{' '}
                entries.
              </p>
              <p className="series-note">
                Both contributions measure total entries. Rounded figures may differ slightly. This
                describes arithmetic, not causes.
                {latest!.year - previous!.year > 1 &&
                  ' Calendar years between these editions are unavailable.'}
              </p>
            </>
          ) : (
            <p className="series-note">
              No decomposition for provisional editions or an edition without counted entrants.
            </p>
          )}
        </Panel>
      </div>
      <Panel
        title="Where demand is moving"
        description={`Largest absolute changes among ${report.trends.length} selected classes${previous && latest ? ` · ${previous.year} to ${latest.year}` : ''}`}
        aside={
          <Button variant="ghost" className="series-soft-button" onClick={() => explore()}>
            Explore classes <ArrowRight />
          </Button>
        }
      >
        {!movers.length ? (
          <p className="series-note">
            Select class identities with confirmed history in both editions to compare demand.
          </p>
        ) : (
          movers.map(t => (
            <button
              key={t.identity.id}
              className="w-full rounded-md text-left p-3 hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring"
              onClick={() => explore(t.identity.id)}
            >
              <span className="flex items-center justify-between gap-4">
                <span>
                  <strong className="block">{t.identity.label}</strong>
                  <span className="series-note">
                    {t.prior.count} → {t.current.count} entries <ClassBadge cell={t.current} />
                  </span>
                </span>
                <strong>
                  {t.delta > 0 ? '+' : ''}
                  {t.delta}
                </strong>
              </span>
              <span className="series-bar mt-3">
                <span
                  style={{
                    width: `${(100 * Math.abs(t.delta)) / Math.max(1, ...movers.map(x => Math.abs(x.delta)))}%`,
                    background: palette[category(t.current)] ?? teal,
                  }}
                />
              </span>
            </button>
          ))
        )}
      </Panel>
      <Panel
        title="Annual participation"
        description="Exact values and class-alignment corrections"
      >
        <AnnualTable report={report} seriesId={seriesId} />
      </Panel>
    </div>
  )
}

function ClassHistory({ trend, report }: { trend: Trend; report: Report }) {
  const last = trend.cells.filter(c => c.class).at(-1)?.class ?? null
  return (
    <div className="space-y-5">
      <ClassBadge cell={last} />
      <ChartContainer
        role="group"
        config={config}
        className="h-56 w-full"
        aria-label={`${trend.identity.label} entries; exact values below`}
      >
        <LineChart
          data={trend.cells.map(c => ({ year: c.year, entries: c.class?.count ?? null }))}
          accessibilityLayer
        >
          <CartesianGrid vertical={false} />
          <XAxis dataKey="year" {...axis} />
          <YAxis {...axis} width={40} />
          <ChartTooltip content={<ChartTooltipContent />} />
          <Line
            dataKey="entries"
            stroke={palette[category(last)] ?? teal}
            strokeWidth={2}
            connectNulls={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ChartContainer>
      <DataTable
        caption={`${trend.identity.label}: historical class details`}
        headers={['Year', 'Original class / effective name', 'Division', 'Entries']}
        rows={trend.cells.map(c => {
          const edition = report.years.find(y => y.year === c.year)?.edition
          return [
            c.year,
            c.class ? (
              <>
                {c.class.code}: {c.class.originalName}
                {c.class.effectiveName !== c.class.originalName && (
                  <span className="block">Display name: {c.class.effectiveName}</span>
                )}
              </>
            ) : !edition ? (
              'No available edition data'
            ) : (
              `No confirmed class${edition.metrics.coverage.unresolved.classes ? ' — alignment incomplete' : ''}`
            ),
            c.class ? `${c.class.division.code}: ${c.class.division.name}` : '—',
            c.class ? c.class.count : '—',
          ]
        })}
      />
    </div>
  )
}
function Classes({ report, select }: { report: Report; select: (id: string) => void }) {
  const [search, setSearch] = useReportFilter('classSearch', ''),
    [division, setDivision] = useReportFilter('division', 'all'),
    [sort, setSort] = useState('latest'),
    [mode, setMode] = useState('count'),
    [colors, setColors] = useState(true)
  const latestYear = report.years.filter(y => y.edition).at(-1)?.year
  const divisions = [
    ...new Set(
      report.trends.flatMap(t => t.cells.flatMap(c => (c.class ? [c.class.division.name] : [])))
    ),
  ].sort()
  const rows = report.trends.filter(
    t =>
      (t.identity.label.toLowerCase().includes(search.toLowerCase()) ||
        t.cells.some(
          c =>
            c.class &&
            `${c.class.code} ${c.class.originalName} ${c.class.effectiveName}`
              .toLowerCase()
              .includes(search.toLowerCase())
        )) &&
      (division === 'all' || t.cells.some(c => c.class?.division.name === division))
  )
  rows.sort((a, b) =>
    sort === 'name'
      ? a.identity.label.localeCompare(b.identity.label)
      : (b.cells.find(c => c.year === latestYear)?.class?.count ?? -1) -
          (a.cells.find(c => c.year === latestYear)?.class?.count ?? -1) ||
        a.identity.label.localeCompare(b.identity.label)
  )
  function value(c: Trend['cells'][number]) {
    const total = report.years.find(y => y.year === c.year)?.edition?.metrics.entriesCount ?? 0
    return c.class ? (mode === 'count' ? c.class.count : share(c.class.count, total)) : null
  }
  const max = Math.max(1, ...rows.flatMap(t => t.cells.map(c => value(c) ?? 0)))
  const utilization = report.years.map(y => ({
    year: y.year,
    active: y.edition?.analytics.activeClasses ?? null,
    empty: y.edition
      ? y.edition.analytics.offeredClasses - y.edition.analytics.activeClasses
      : null,
  }))
  return (
    <div className="space-y-5">
      <Panel
        title="Class history at a glance"
        description="Matched classes across years. Original names are preserved; gaps are not zero."
        aside={
          <Button
            variant="outline"
            className="series-soft-button"
            aria-pressed={colors}
            onClick={() => setColors(!colors)}
          >
            Division colors {colors ? 'on' : 'off'}
          </Button>
        }
      >
        <div className="series-controls">
          <Input
            aria-label="Find a class"
            placeholder="Find a class…"
            className="max-w-xs"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select
            className="series-select"
            aria-label="Division"
            value={division}
            onChange={e => setDivision(e.target.value)}
          >
            <option value="all">All divisions</option>
            {divisions.map(d => (
              <option key={d}>{d}</option>
            ))}
          </select>
          <select
            className="series-select"
            aria-label="Sort classes"
            value={sort}
            onChange={e => setSort(e.target.value)}
          >
            <option value="latest">Latest entries</option>
            <option value="name">Class name</option>
          </select>
          <div role="group" aria-label="Heatmap measure" className="series-controls">
            {(
              [
                ['count', 'Entry count'],
                ['share', '% of edition'],
              ] as const
            ).map(([value, label]) => (
              <Button
                key={value}
                size="sm"
                variant="outline"
                className="series-soft-button"
                aria-pressed={mode === value}
                onClick={() => setMode(value)}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
        {!rows.length ? (
          <p className="series-note">
            {report.trends.length
              ? 'No selected classes match these filters.'
              : 'No classes selected. Choose classes above to see their history.'}
          </p>
        ) : (
          <Table tabIndex={0}>
            <caption className="sr-only">
              Class history heatmap —{' '}
              {mode === 'count' ? 'entry count' : 'share of all edition entries'}
            </caption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col" className="series-sticky">
                  Shared class identity
                </TableHead>
                {report.years.map(y => (
                  <TableHead scope="col" key={y.year}>
                    {y.year}
                    {y.edition?.provisional && <span className="block text-xs">Provisional</span>}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(t => {
                const last = t.cells.filter(c => c.class).at(-1)?.class ?? null
                return (
                  <TableRow key={t.identity.id}>
                    <TableHead scope="row" className="series-sticky">
                      <button
                        className="text-left py-2 pl-2 border-l-3 focus-visible:outline-2 focus-visible:outline-ring"
                        style={{
                          borderColor: colors
                            ? (palette[category(last)] ?? 'transparent')
                            : 'transparent',
                        }}
                        onClick={() => select(t.identity.id)}
                        aria-label={`Inspect ${t.identity.label}`}
                      >
                        <span className="block font-semibold underline underline-offset-4">
                          {t.identity.label}
                          {t.identity.retired && ' (retired)'}
                        </span>
                        {colors ? (
                          <ClassBadge cell={last} />
                        ) : (
                          <span className="series-note">
                            {last?.code} · {last?.division.name}
                          </span>
                        )}
                      </button>
                      <SeriesModelLink identity={t.identity} />
                    </TableHead>
                    {t.cells.map(c => {
                      const n = value(c)
                      return (
                        <TableCell key={c.year}>
                          <div
                            className={`series-heat flex items-center justify-center ${n === null ? 'series-gap' : ''}`}
                            style={
                              n === null
                                ? undefined
                                : {
                                    backgroundColor: `color-mix(in srgb, #147d73 ${8 + (38 * n) / max}%, white)`,
                                  }
                            }
                            aria-label={`${c.year}: ${n === null ? (c.class ? 'Share unavailable: no counted entries' : 'No confirmed class or available edition') : `${format(n)}${mode === 'share' ? '%' : ' entries'}`}`}
                          >
                            {n === null ? '—' : `${format(n)}${mode === 'share' ? '%' : ''}`}
                          </div>
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
        <p className="series-note">
          One magnitude scale across all classes. Shares use every counted entry in that edition,
          including unresolved and unmatched classes. Division filters match any visible historical
          edition.
        </p>
      </Panel>
      <Panel
        title="Schedule utilization"
        description="All offered classes, independent of the class filters above"
      >
        <ChartContainer
          role="group"
          config={config}
          className="h-72 w-full"
          aria-label="Active and zero-entry classes; exact values in table"
        >
          <BarChart data={utilization} accessibilityLayer>
            <CartesianGrid vertical={false} />
            <XAxis {...axis} dataKey="year" />
            <YAxis {...axis} allowDecimals={false} width={40} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Bar dataKey="active" stackId="classes" fill={teal} isAnimationActive={false} />
            <Bar dataKey="empty" stackId="classes" fill="#b8cdc6" isAnimationActive={false} />
          </BarChart>
        </ChartContainer>
        <p className="series-note">
          Teal: at least one counted entry. Pale: offered with zero counted entries. Missing
          editions have no bar.
        </p>
        <TableDetail>
          <DataTable
            caption="Schedule utilization"
            headers={['Year', 'Active', 'Zero-entry', 'Utilization']}
            rows={utilization.map(y => [
              y.year,
              format(y.active),
              format(y.empty),
              y.active === null || y.empty === null
                ? 'Unavailable'
                : percent(share(y.active, y.active + y.empty)),
            ])}
          />
        </TableDetail>
      </Panel>
    </div>
  )
}
function Participation({ report }: { report: Report }) {
  const editions = report.years.filter(
    (y): y is Year & { edition: NonNullable<Year['edition']> } => !!y.edition
  )
  const [year, setYear] = useState('latest')
  const selected = editions.find(y => String(y.year) === year) ?? editions.at(-1)
  if (!selected) return <p>No available edition data for participation.</p>
  const { metrics: m, analytics: a } = selected.edition
  const curve = a.concentration.map(p => ({
    entrantShare: share(p.entrants, m.entrantsCount),
    entryShare: share(p.entries, m.entriesCount),
    ...p,
  }))
  return (
    <div className="space-y-5">
      <div className="series-controls">
        <label htmlFor="participation-edition">Edition</label>
        <select
          id="participation-edition"
          className="series-select"
          value={String(selected.year)}
          onChange={e => setYear(e.target.value)}
        >
          {editions.map(y => (
            <option key={y.year} value={y.year}>
              {y.year}
              {y.edition.provisional ? ' · Provisional' : ''}
            </option>
          ))}
        </select>
        <span className="series-note">
          {format(m.entrantsCount)} entrants · {format(m.entriesCount)} counted entries
        </span>
      </div>
      <div className="series-kpis">
        {[
          ['Mean entries', format(m.averageEntriesPerEntrant)],
          ['Median entries', format(a.medianEntriesPerEntrant)],
          ['Top 10% share', percent(share(a.topDecile.entries, m.entriesCount))],
          ['Active classes', `${a.activeClasses} / ${a.offeredClasses}`],
        ].map(([label, value]) => (
          <div className="series-kpi" key={label}>
            <p className="series-note">{label}</p>
            <p className="series-value">{value}</p>
          </div>
        ))}
      </div>
      <div className="series-grid">
        <Panel
          title="Entries per entrant"
          description="Distribution among entrants with at least one counted entry"
        >
          <ChartContainer
            role="group"
            config={config}
            className="h-72 w-full"
            aria-label="Entries per entrant histogram; exact values in table"
          >
            <BarChart data={a.histogram} accessibilityLayer>
              <CartesianGrid vertical={false} />
              <XAxis {...axis} dataKey="label" />
              <YAxis {...axis} allowDecimals={false} width={40} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="entrants" fill={teal} radius={[5, 5, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ChartContainer>
          <p className="series-note">
            X: entries per entrant. Y: number of entrants. Mean and median describe this edition
            only.
          </p>
          <TableDetail>
            <DataTable
              caption="Entries per entrant distribution"
              headers={['Entries per entrant', 'Entrants', 'Entries']}
              rows={a.histogram.map(b => [b.label, b.entrants, b.entries])}
            />
          </TableDetail>
        </Panel>
        <Panel
          title="How concentrated is participation?"
          description={`${a.topDecile.entrants} entrants (rounded-up top 10%) contributed ${a.topDecile.entries} entries`}
        >
          {m.entriesCount ? (
            <ChartContainer
              role="group"
              config={config}
              className="h-72 w-full"
              aria-label="Cumulative entry share; exact values in table"
            >
              <LineChart data={curve} accessibilityLayer>
                <CartesianGrid />
                <XAxis {...axis} dataKey="entrantShare" type="number" domain={[0, 100]} unit="%" />
                <YAxis {...axis} domain={[0, 100]} unit="%" width={45} />
                <ReferenceLine
                  segment={[
                    { x: 0, y: 0 },
                    { x: 100, y: 100 },
                  ]}
                  stroke="#94a3b8"
                  strokeDasharray="4 4"
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Line
                  dataKey="entryShare"
                  stroke={teal}
                  strokeWidth={2}
                  dot
                  isAnimationActive={false}
                />
              </LineChart>
            </ChartContainer>
          ) : (
            <p className="series-note">No counted entries; concentration is unavailable.</p>
          )}
          <p className="series-note">
            Entrants ordered from fewest to most entries. X: cumulative entrant share; Y: cumulative
            entry share. The diagonal is equal contribution, not a target. Up to ten grouped steps;
            no person records.
          </p>
          <TableDetail>
            <DataTable
              caption="Cumulative participation"
              headers={['Entrants', 'Entries', 'Entrant share', 'Entry share']}
              rows={curve.map(p => [
                p.entrants,
                p.entries,
                percent(p.entrantShare),
                percent(p.entryShare),
              ])}
            />
          </TableDetail>
        </Panel>
      </div>
      <Panel
        title="Affiliation mix"
        description="Top five stored affiliation groups; the denominator includes entries without an affiliation"
      >
        {a.affiliations === null ? (
          <p className="series-note">
            Club tracking was disabled for this edition. Affiliation data is unavailable.
          </p>
        ) : (
          <>
            <DataTable
              caption="Affiliation mix"
              headers={['Stored affiliation', 'Entries', 'Share of all entries']}
              rows={a.affiliations.map(g => [
                g.label,
                g.entries,
                <div className="min-w-28">
                  <span>{percent(share(g.entries, m.entriesCount))}</span>
                  <div className="series-bar mt-1">
                    <span style={{ width: `${share(g.entries, m.entriesCount) ?? 0}%` }} />
                  </div>
                </div>,
              ])}
            />
            <p className="series-note">
              Stored club ID first, otherwise normalized manual name. Identical labels can represent
              different keys. Current club names and entrant affiliations are not substituted.
            </p>
          </>
        )}
      </Panel>
    </div>
  )
}
function Coverage({ report, seriesId }: { report: Report; seriesId: string }) {
  return (
    <div className="space-y-5">
      <Panel
        title="Entry alignment coverage"
        description="Confirmed-assignment entries divided by all counted entries"
      >
        <div className="space-y-5">
          {report.years.map(y => {
            const m = y.edition?.metrics,
              ratio = m ? share(m.coverage.assigned.entries, m.entriesCount) : null
            return (
              <div key={y.year}>
                <div className="flex justify-between gap-3 text-sm mb-2">
                  <span>
                    {y.year}{' '}
                    {y.edition?.provisional && <Badge variant="outline">Provisional</Badge>}
                  </span>
                  <span>{percent(ratio)}</span>
                </div>
                <div className="series-bar">
                  <span style={{ width: `${ratio ?? 0}%` }} />
                </div>
              </div>
            )
          })}
        </div>
        <p className="series-note">
          Alignment coverage is not proof of historical data completeness or semantic correctness.
          No counted entries means no coverage percentage.
        </p>
      </Panel>
      <Panel
        title="Edition coverage and corrections"
        description="Unreadable and missing editions share the same unavailable state"
      >
        <AnnualTable report={report} seriesId={seriesId} />
      </Panel>
      <Panel title="Measurement notes">
        <dl className="space-y-4 text-sm">
          <div>
            <dt className="font-semibold">Counted population</dt>
            <dd>
              All persisted entries except not-entered, including drafts. Entrants are distinct
              exhibitor IDs within one edition; annual entrant counts are not unique people across
              years.
            </dd>
          </div>
          <div>
            <dt className="font-semibold">Class continuity</dt>
            <dd>
              Only confirmed assignments contribute. Overall totals include unresolved and
              explicitly unmatched classes. Splits and mergers are never automatically combined.
            </dd>
          </div>
          <div>
            <dt className="font-semibold">Unavailable is not zero</dt>
            <dd>
              Missing or unreadable editions, absent class assignments, disabled clubs and zero
              denominators retain distinct unavailable states. Offered classes without counted
              entries are zero.
            </dd>
          </div>
          <div>
            <dt className="font-semibold">Live staff data</dt>
            <dd>
              These views reflect current persisted facts. Historical completeness, cross-year
              person retention, payments and comparable judging scores are not established by this
              report.
            </dd>
          </div>
        </dl>
      </Panel>
    </div>
  )
}
export function SeriesDashboard({
  report: source,
  seriesId,
}: {
  report: Report
  seriesId: string
}) {
  const [view, setView] = useReportFilter('tab', 'overview'),
    [provisional, setProvisional] = useState(true),
    [detail, setDetail] = useState<string | null>(null)
  const report = visibleReport(source, provisional)
  const selected = report.trends.find(t => t.identity.id === detail)
  const available = report.years.filter(y => y.edition).length
  const excluded = source.years.filter(y => !provisional && y.edition?.provisional).length
  function exportCsv() {
    const rows = [
      ['Year', 'Edition', 'Status', ...metricLabels.map(m => m[1])],
      ...report.years.map(y => [
        y.year,
        y.edition?.name ?? 'No available edition data',
        y.edition ? (y.edition.provisional ? 'Provisional' : 'Finalized') : '',
        ...metricLabels.map(([key]) => y.edition?.metrics[key] ?? ''),
      ]),
    ]
    // Quote every cell and neutralize spreadsheet formula prefixes in user-entered names.
    const csv = rows.map(row => row.map(csvCell).join(',')).join('\r\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
    const link = document.createElement('a')
    link.href = url
    link.download = `series-participation-${report.fromYear}-${report.toYear}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }
  return (
    <div className="series-dashboard space-y-5">
      <div className="series-controls border-y py-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={provisional}
            onChange={e => setProvisional(e.target.checked)}
          />
          Include provisional
        </label>
        <span className="series-note">
          {available} available editions{excluded > 0 ? ` · ${excluded} provisional excluded` : ''}
        </span>
        <Button variant="outline" className="series-soft-button ml-auto" onClick={exportCsv}>
          <Download />
          Export annual CSV
        </Button>
      </div>
      <Tabs value={view} onValueChange={setView}>
        <TabsList aria-label="Series dashboard views">
          <TabsTrigger value="overview">Series overview</TabsTrigger>
          <TabsTrigger value="classes">Class explorer</TabsTrigger>
          <TabsTrigger value="participation">Participation</TabsTrigger>
          <TabsTrigger value="coverage">Data coverage</TabsTrigger>
        </TabsList>
        <TabsContent value="overview" className="mt-5">
          <Overview
            report={report}
            seriesId={seriesId}
            explore={id => {
              setView('classes')
              if (id) setDetail(id)
            }}
          />
        </TabsContent>
        <TabsContent value="classes" className="mt-5">
          <Classes report={report} select={setDetail} />
        </TabsContent>
        <TabsContent value="participation" className="mt-5">
          <Participation report={report} />
        </TabsContent>
        <TabsContent value="coverage" className="mt-5">
          <Coverage report={report} seriesId={seriesId} />
        </TabsContent>
      </Tabs>
      <Sheet
        open={!!selected}
        onOpenChange={open => {
          if (!open) setDetail(null)
        }}
      >
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{selected?.identity.label}</SheetTitle>
            <SheetDescription>
              Matched class history; original class names preserved.
            </SheetDescription>
          </SheetHeader>
          {selected && (
            <div className="px-4 pb-6">
              <ClassHistory trend={selected} report={report} />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}
